import { useEffect, useState, useCallback } from "react";
import { Helmet } from "react-helmet";
import {
  Box,
  Container,
  Card,
  CardHeader,
  CardContent,
  Button,
  Grid,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Typography,
  Chip,
  Tooltip,
  Snackbar,
  CircularProgress,
  Divider,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Close,
  Sync,
  Add,
  Delete,
  FileCopy,
  CheckCircle,
  Error as ErrorIcon,
  Schedule,
  Launch,
} from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "src/utils/api";

// ── Platform metadata ──

const PLATFORMS = {
  airbnb: {
    name: "Airbnb",
    color: "#FF5A5F",
    guide:
      "Go to Calendar → Availability Settings → Sync calendars → Export Calendar. Copy the URL.",
  },
  vrbo: {
    name: "VRBO",
    color: "#3E59A8",
    guide:
      "Go to Calendar → Import/Export → Export Calendar. Copy the iCal link.",
  },
  booking: {
    name: "Booking.com",
    color: "#003580",
    guide: "Go to Property → Calendar → Sync calendars. Copy the export link.",
  },
  google: {
    name: "Google Calendar",
    color: "#4285F4",
    guide:
      'In Google Calendar, click the three dots on the calendar → Settings → "Secret address in iCal format". Copy that URL.',
  },
  other: {
    name: "Other",
    color: "#6b7280",
    guide:
      "Find the iCal export / .ics URL in your platform's calendar settings.",
  },
};

// ── Styles ──

const useStyles = makeStyles((theme) => ({
  sectionCard: {
    marginBottom: theme.spacing(3),
  },
  exportCard: {
    padding: theme.spacing(2),
    backgroundColor: "#f8fffe",
    border: "1px solid #d1e7e4",
    borderRadius: 10,
    marginBottom: theme.spacing(2),
  },
  exportUrl: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    padding: "8px 12px",
    backgroundColor: "#fff",
    borderRadius: 6,
    border: "1px solid #e5e7eb",
    fontFamily: "monospace",
    fontSize: 13,
    wordBreak: "break-all",
    color: "#374151",
  },
  connectionCard: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2),
    borderRadius: 10,
    border: "1px solid #e5e7eb",
    marginBottom: theme.spacing(1.5),
    transition: "border-color 0.2s",
    "&:hover": {
      borderColor: "#d1d5db",
    },
  },
  platformBadge: {
    fontWeight: 600,
    fontSize: 12,
    letterSpacing: 0.3,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    display: "inline-block",
    marginRight: 6,
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(5),
    color: "#9ca3af",
  },
  guideBox: {
    padding: theme.spacing(2),
    backgroundColor: "#f9fafb",
    borderRadius: 8,
    border: "1px dashed #d1d5db",
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
}));

// ── Export URL card ──

function ExportUrlCard({ home, classes }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(home.export_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Box className={classes.exportCard}>
      <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
        {home.title}
      </Typography>
      <Typography variant="caption" color="textSecondary">
        Share this URL with Airbnb, VRBO, Booking.com, etc. to export your
        Tubayo calendar
      </Typography>
      <Box className={classes.exportUrl}>
        <Box style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>
          {home.export_url}
        </Box>
        <Tooltip title={copied ? "Copied!" : "Copy URL"}>
          <IconButton
            size="small"
            onClick={handleCopy}
            style={{ color: copied ? "#0d9488" : "#6b7280" }}
          >
            {copied ? (
              <CheckCircle fontSize="small" />
            ) : (
              <FileCopy fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

// ── Connection row ──

function ConnectionRow({ connection, onRefresh, onDelete, classes }) {
  const platform = PLATFORMS[connection.platform] || PLATFORMS.other;
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    await onRefresh(connection.id);
    setRefreshing(false);
  };

  const statusColor =
    connection.last_sync_status === "success"
      ? "#10b981"
      : connection.last_sync_status === "error"
        ? "#ef4444"
        : "#f59e0b";

  const lastSynced = connection.last_synced_at
    ? new Date(connection.last_synced_at).toLocaleString()
    : "Never";

  return (
    <Box className={classes.connectionCard}>
      <Box style={{ flex: 1 }}>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <Chip
            label={platform.name}
            size="small"
            className={classes.platformBadge}
            style={{
              backgroundColor: platform.color,
              color: "#fff",
            }}
          />
          <Typography variant="body2" style={{ fontWeight: 500 }}>
            {connection.label || connection.home_title}
          </Typography>
        </Box>
        <Box
          display="flex"
          alignItems="center"
          style={{ gap: 12, marginTop: 6 }}
        >
          <Typography
            variant="caption"
            style={{ display: "flex", alignItems: "center" }}
          >
            <span
              className={classes.statusDot}
              style={{ backgroundColor: statusColor }}
            />
            {connection.last_sync_message || "Pending first sync"}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            <Schedule
              style={{ fontSize: 12, verticalAlign: "middle", marginRight: 2 }}
            />
            {lastSynced}
          </Typography>
        </Box>
      </Box>

      <Box display="flex" style={{ gap: 4 }}>
        <Tooltip title="Sync now">
          <IconButton
            size="small"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            {refreshing ? (
              <CircularProgress size={18} />
            ) : (
              <Sync fontSize="small" style={{ color: "#0d9488" }} />
            )}
          </IconButton>
        </Tooltip>
        <Tooltip title="Disconnect">
          <IconButton
            size="small"
            onClick={() =>
              onDelete(connection.id, connection.label || platform.name)
            }
          >
            <Delete fontSize="small" style={{ color: "#ef4444" }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

// ── Add connection dialog ──

function AddConnectionDialog({ open, onClose, homes, onSubmit }) {
  const classes = useStyles();
  const [form, setForm] = useState({
    homeId: "",
    platform: "",
    icalUrl: "",
    label: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.homeId || !form.icalUrl) {
      toast.warning("Select a property and paste the calendar URL.");
      return;
    }
    setSubmitting(true);
    await onSubmit(form);
    setSubmitting(false);
    setForm({ homeId: "", platform: "", icalUrl: "", label: "" });
  };

  const selectedPlatform = PLATFORMS[form.platform];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Import External Calendar</Typography>
          <IconButton size="small" onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Paste an iCal (.ics) URL from another platform to automatically sync
          their bookings into your Tubayo calendar.
        </Typography>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2} style={{ marginTop: 8 }}>
            <Grid item xs={12}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Property</InputLabel>
                <Select
                  name="homeId"
                  value={form.homeId}
                  onChange={handleChange}
                  label="Property"
                  required
                >
                  {homes.map((h) => (
                    <MenuItem key={h.id} value={h.id}>
                      {h.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Platform</InputLabel>
                <Select
                  name="platform"
                  value={form.platform}
                  onChange={handleChange}
                  label="Platform"
                >
                  {Object.entries(PLATFORMS).map(([key, val]) => (
                    <MenuItem key={key} value={key}>
                      {val.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Platform-specific guide */}
            {selectedPlatform && (
              <Grid item xs={12}>
                <Box className={classes.guideBox}>
                  <Typography
                    variant="caption"
                    style={{
                      fontWeight: 600,
                      display: "block",
                      marginBottom: 4,
                    }}
                  >
                    How to get the URL from {selectedPlatform.name}:
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {selectedPlatform.guide}
                  </Typography>
                </Box>
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                label="Calendar URL"
                name="icalUrl"
                value={form.icalUrl}
                onChange={handleChange}
                placeholder="https://www.airbnb.com/calendar/ical/12345.ics?s=abc123"
                fullWidth
                size="small"
                variant="outlined"
                required
                helperText="The .ics or iCal export URL from the external platform"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Label (optional)"
                name="label"
                value={form.label}
                onChange={handleChange}
                placeholder="e.g. My Airbnb listing"
                fullWidth
                size="small"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" style={{ gap: 8 }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={submitting}
                  startIcon={
                    submitting ? (
                      <CircularProgress size={16} style={{ color: "#fff" }} />
                    ) : (
                      <Add />
                    )
                  }
                  style={{
                    backgroundColor: "#0d9488",
                    color: "#fff",
                    opacity: submitting ? 0.7 : 1,
                  }}
                >
                  {submitting ? "Connecting..." : "Connect & Sync"}
                </Button>
                <Button variant="outlined" onClick={onClose}>
                  Cancel
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ══════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════

function CalendarSync() {
  const classes = useStyles();
  const [connections, setConnections] = useState([]);
  const [homes, setHomes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`homes/host/calendar/sync/`);
      setConnections(res.data.connections);
      setHomes(res.data.homes);
    } catch (err) {
      toast.error("Failed to load sync connections.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAdd = async (form) => {
    try {
      await api.post(`homes/host/calendar/sync/`, {
        home_id: form.homeId,
        platform: form.platform || "other",
        ical_url: form.icalUrl,
        label: form.label,
      });
      toast.success("Calendar connected and synced!");
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      const msg = err.response?.data?.error || "Failed to connect calendar.";
      toast.error(msg);
    }
  };

  const handleRefresh = async (connectionId) => {
    try {
      const res = await api.post(
        `homes/host/calendar/sync/${connectionId}/refresh/`,
      );
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error("Sync failed. Check the URL and try again.");
    }
  };

  const handleDelete = async (connectionId, name) => {
    if (!window.confirm(`Disconnect "${name}"? Synced blocks will be removed.`))
      return;
    try {
      await api.delete(`homes/host/calendar/sync/${connectionId}/`);
      toast.success("Calendar disconnected.");
      fetchData();
    } catch (err) {
      toast.error("Failed to disconnect.");
    }
  };

  return (
    <>
      <Helmet>
        <title>Tubayo Host | Calendar Sync</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: "background.default",
          minHeight: "100%",
          py: 3,
        }}
      >
        <Container maxWidth={false}>
          {/* ── Export Section ── */}
          <Card className={classes.sectionCard}>
            <CardHeader
              title="Export Your Tubayo Calendar"
              subheader="Share these URLs with Airbnb, VRBO, or Booking.com so they see your Tubayo bookings"
            />
            <CardContent>
              {loading ? (
                <CircularProgress size={24} />
              ) : homes.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  No properties found. Add a home first.
                </Typography>
              ) : (
                homes.map((home) => (
                  <ExportUrlCard key={home.id} home={home} classes={classes} />
                ))
              )}

              <Box
                style={{
                  marginTop: 16,
                  padding: 16,
                  backgroundColor: "#fffbeb",
                  borderRadius: 8,
                  border: "1px solid #fde68a",
                }}
              >
                <Typography
                  variant="caption"
                  style={{ fontWeight: 600, display: "block", marginBottom: 4 }}
                >
                  How to use this
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Copy the URL above and paste it into your Airbnb, VRBO, or
                  Booking.com calendar import settings. They'll automatically
                  pull your Tubayo bookings and blocked dates. Updates sync
                  approximately every 3 hours.
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* ── Import Section ── */}
          <Card className={classes.sectionCard}>
            <CardHeader
              title="Import External Calendars"
              subheader="Connect calendars from Airbnb, VRBO, Booking.com, or Google Calendar to block their booked dates on Tubayo"
              action={
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={() => setDialogOpen(true)}
                  style={{ borderColor: "#0d9488", color: "#0d9488" }}
                >
                  Add Calendar
                </Button>
              }
            />
            <CardContent>
              {loading ? (
                <CircularProgress size={24} />
              ) : connections.length === 0 ? (
                <Box className={classes.emptyState}>
                  <Sync
                    style={{ fontSize: 48, color: "#d1d5db", marginBottom: 8 }}
                  />
                  <Typography
                    variant="body1"
                    style={{ fontWeight: 500, color: "#6b7280" }}
                  >
                    No calendars connected yet
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Import your Airbnb or VRBO calendar to prevent double
                    bookings
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setDialogOpen(true)}
                    style={{
                      marginTop: 16,
                      backgroundColor: "#0d9488",
                      color: "#fff",
                    }}
                  >
                    Connect Your First Calendar
                  </Button>
                </Box>
              ) : (
                connections.map((conn) => (
                  <ConnectionRow
                    key={conn.id}
                    connection={conn}
                    onRefresh={handleRefresh}
                    onDelete={handleDelete}
                    classes={classes}
                  />
                ))
              )}
            </CardContent>
          </Card>
        </Container>
      </Box>

      <AddConnectionDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        homes={homes}
        onSubmit={handleAdd}
      />
    </>
  );
}

export default CalendarSync;
